import { useState, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, Edit, ToggleLeft, ToggleRight } from 'lucide-react';
import { useProductRules, useCreateProductRule, useUpdateProductRule } from '../../hooks/useRules';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import type { ProductRule, CreateProductRuleRequest } from '../../api/rules';

const columnHelper = createColumnHelper<ProductRule>();

export default function ProductRulesPage() {
  const { data: rules, isLoading } = useProductRules();
  const createRule = useCreateProductRule();
  const updateRule = useUpdateProductRule();
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<ProductRule | null>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{info.getValue()}</span>,
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => <span className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[300px] block">{info.getValue() || '-'}</span>,
      }),
      columnHelper.accessor('enabled', {
        header: 'Enabled',
        cell: (info) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              updateRule.mutate({ urn: info.row.original.urn, data: { enabled: !info.getValue() } as Partial<CreateProductRuleRequest> });
            }}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            {info.getValue() ? (
              <ToggleRight className="h-5 w-5 text-green-500" />
            ) : (
              <ToggleLeft className="h-5 w-5 text-gray-400" />
            )}
          </button>
        ),
      }),
      columnHelper.accessor('weight', {
        header: 'Weight',
        cell: (info) => <span className="text-sm dark:text-gray-200">{info.getValue()}</span>,
      }),
      columnHelper.accessor('conditions', {
        header: 'Conditions',
        cell: (info) => <span className="text-sm text-gray-500 dark:text-gray-400">{info.getValue()?.length || 0}</span>,
      }),
      columnHelper.accessor('targets', {
        header: 'Targets',
        cell: (info) => <span className="text-sm text-gray-500 dark:text-gray-400">{info.getValue()?.length || 0}</span>,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditingRule(info.row.original);
              setShowForm(true);
            }}
            className="btn-icon"
          >
            <Edit className="h-4 w-4" />
          </button>
        ),
      }),
    ],
    [updateRule],
  );

  return (
    <div>
      <PageHeader
        title="Product Rules"
        description="Rules for product recommendation based on loan conditions"
        actions={
          <button
            onClick={() => {
              setEditingRule(null);
              setShowForm(true);
            }}
            className="btn-primary gap-1"
          >
            <Plus className="h-4 w-4" />
            New Rule
          </button>
        }
      />

      {showForm && (
        <ProductRuleForm
          rule={editingRule}
          onSubmit={(data) => {
            if (editingRule) {
              updateRule.mutate(
                { urn: editingRule.urn, data },
                { onSuccess: () => setShowForm(false) },
              );
            } else {
              createRule.mutate(data as CreateProductRuleRequest, {
                onSuccess: () => setShowForm(false),
              });
            }
          }}
          onCancel={() => setShowForm(false)}
          isPending={createRule.isPending || updateRule.isPending}
        />
      )}

      <DataTable data={rules || []} columns={columns} isLoading={isLoading} />
    </div>
  );
}

function ProductRuleForm({
  rule,
  onSubmit,
  onCancel,
  isPending,
}: {
  rule: ProductRule | null;
  onSubmit: (data: Partial<CreateProductRuleRequest>) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [name, setName] = useState(rule?.name || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [weight, setWeight] = useState(rule?.weight || 1);
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [conditionsJson, setConditionsJson] = useState(
    JSON.stringify(rule?.conditions || [], null, 2),
  );
  const [targetsJson, setTargetsJson] = useState(
    JSON.stringify(rule?.targets || [], null, 2),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      onSubmit({
        name,
        description,
        weight,
        enabled,
        conditions: JSON.parse(conditionsJson),
        targets: JSON.parse(targetsJson),
      });
    } catch {
      alert('Invalid JSON in conditions or targets');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card p-4 lg:p-6 mb-4 lg:mb-6">
      <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {rule ? 'Edit Rule' : 'New Rule'}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4 mb-4">
        <div>
          <label className="label">Name *</label>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="label">Weight</label>
          <input
            type="number"
            className="input"
            value={weight}
            onChange={(e) => setWeight(Number(e.target.value))}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Description</label>
          <input
            type="text"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <input
            type="checkbox"
            id="rule-enabled"
            className="rounded"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label htmlFor="rule-enabled" className="text-sm text-gray-700 dark:text-gray-200">
            Enabled
          </label>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4 mb-4">
        <div>
          <label className="label">Conditions (JSON)</label>
          <textarea
            className="input font-mono text-xs h-40"
            value={conditionsJson}
            onChange={(e) => setConditionsJson(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Targets (JSON)</label>
          <textarea
            className="input font-mono text-xs h-40"
            value={targetsJson}
            onChange={(e) => setTargetsJson(e.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" disabled={isPending} className="btn-primary">
          {isPending ? 'Saving...' : 'Save Rule'}
        </button>
      </div>
    </form>
  );
}
